import type { Attachment, Message, PendingMessage } from '../types';
import type { DshSessionEntry } from './dsh-session-projection';

export interface DshSessionOutgoing {
  id: string;
  content: string;
  contentType: Message['content_type'];
  createdAt: number;
  status: 'pending' | 'failed' | 'sent';
  attachments: Attachment[];
  message?: Message;
  pendingMessage?: PendingMessage;
  nativeEchoed?: boolean;
  nativeEntryId?: string;
}

interface SelectionInput {
  channelId: string;
  userId: string;
  messages: readonly Message[];
  pending: readonly PendingMessage[];
  entries: readonly DshSessionEntry[];
}

export type DshSessionTimelineRow =
  | { kind: 'entry'; id: string; value: DshSessionEntry }
  | { kind: 'outgoing'; id: string; value: DshSessionOutgoing };

export function mergeDshSessionTimeline(entries: readonly DshSessionEntry[], outgoing: readonly DshSessionOutgoing[]): DshSessionTimelineRow[] {
  const entryIndexes = new Map(entries.map((entry, index) => [entry.id, index]));
  const timestamps = entries.map(({ entry }) => Date.parse(entry.timestamp));
  const slots = new Map<number, DshSessionOutgoing[]>();
  for (const value of [...outgoing].sort(compareOutgoing)) {
    const correlated = value.nativeEntryId ? entryIndexes.get(value.nativeEntryId) : undefined;
    const chronological = correlated === undefined ? timestamps.findIndex(time => Number.isFinite(time) && time >= value.createdAt) : -1;
    const index = correlated ?? (chronological < 0 ? entries.length : chronological);
    const slot = slots.get(index) ?? [];
    slot.push(value);
    slots.set(index, slot);
  }
  // Native sequence remains authoritative; timestamps place only uncorrelated receipts.
  const rows: DshSessionTimelineRow[] = [];
  for (let index = 0; index <= entries.length; index += 1) {
    for (const value of slots.get(index) ?? []) rows.push({ kind: 'outgoing', id: value.id, value });
    const value = entries[index];
    if (value) rows.push({ kind: 'entry', id: value.id, value });
  }
  return rows;
}

export function selectDshSessionOutgoing({ channelId, userId, messages, pending, entries }: SelectionInput): DshSessionOutgoing[] {
  if (!userId) return [];
  const echoedMessages = new Map<string, string>();
  for (const { id, entry } of entries) {
    // Host correlation is the durable message ID, not a task ID or browser send nonce.
    if (entry.item.type === 'user_message' && entry.item.clientMessageId && !echoedMessages.has(entry.item.clientMessageId)) {
      echoedMessages.set(entry.item.clientMessageId, id);
    }
  }
  const sent: DshSessionOutgoing[] = messages
    .filter(message => message.channel_id === channelId && message.sender_id === userId && !message.deleted_at)
    .filter(message => !echoedMessages.has(message.id) || !isPlainTextMessage(message))
    .map(message => ({
      id: `message:${message.id}`, content: message.content, contentType: message.content_type,
      createdAt: message.created_at, status: 'sent', attachments: message.attachments ?? [],
      message,
      nativeEchoed: echoedMessages.has(message.id), nativeEntryId: echoedMessages.get(message.id),
    }));
  const drafts: DshSessionOutgoing[] = pending
    .filter(message => message.channelId === channelId && message.senderId === userId)
    .map(message => ({
      id: `pending:${message.clientMessageId}`, content: message.content, contentType: message.contentType,
      createdAt: message.createdAt, status: message.status, attachments: [],
      pendingMessage: message,
    }));
  return [...sent, ...drafts].sort(compareOutgoing);
}

function isPlainTextMessage(message: Message): boolean {
  return (message.content_type === 'text' || message.content_type === 'command')
    && (!message.type || message.type === 'default') && !message.attachments?.length;
}

function compareOutgoing(left: DshSessionOutgoing, right: DshSessionOutgoing): number {
  return left.createdAt - right.createdAt || left.id.localeCompare(right.id);
}
